import { useState, useEffect, useCallback } from 'react';
import { Shield, ToggleLeft, ToggleRight, Clock, Mail, AlertTriangle, CheckCircle, RefreshCw, Calendar, User, FileText, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

function StatusPill({ on, onLabel, offLabel }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${on ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
      {on ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
      {on ? onLabel : offLabel}
    </span>
  );
}

function LogRow({ log }) {
  const blocked = log.status?.startsWith('blocked') || log.status?.startsWith('error');
  const sent    = log.status === 'sent';
  return (
    <div className={`flex items-start gap-3 px-4 py-3 text-xs border-b border-[#8A9A8B]/8 last:border-0 ${blocked ? 'bg-red-50/40' : sent ? 'bg-green-50/30' : ''}`}
      data-testid="email-log-row">
      <div className="flex-shrink-0 mt-0.5">
        {sent    ? <CheckCircle size={13} className="text-green-500" /> :
         blocked ? <AlertTriangle size={13} className="text-red-400" /> :
                   <Clock size={13} className="text-[#2B2B2B]/30" />}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-bold ${blocked ? 'text-red-600' : sent ? 'text-green-700' : 'text-[#2B2B2B]/60'}`}>
            {log.status || 'unknown'}
          </span>
          {log.trigger_source && (
            <span className="px-1.5 py-0.5 rounded bg-[#2B2B2B]/6 text-[#2B2B2B]/50 font-semibold">
              {log.trigger_source}
            </span>
          )}
          {log.campaign_id && (
            <span className="text-[#8A9A8B]">campaign: {log.campaign_id}</span>
          )}
        </div>
        {log.to && <p className="text-[#2B2B2B]/50">to: {log.to}</p>}
        {log.subject && <p className="text-[#2B2B2B]/60 truncate">"{log.subject}"</p>}
        <div className="flex flex-wrap gap-3 text-[#2B2B2B]/35 pt-0.5">
          {log.caller_fn && <span>{log.caller_fn}() in {log.caller_file}</span>}
          {log.admin_user && <span>admin: {log.admin_user}</span>}
          <span>{fmt(log.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminEmailAutomationSettings({ showMsg }) {
  const [data, setData]       = useState(null);
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, logRes] = await Promise.all([
        fetch(`${API}/admin/email-automation/config`, { credentials: 'include' }),
        fetch(`${API}/admin/email-automation/logs?limit=50`, { credentials: 'include' }),
      ]);
      const cfgData = await cfgRes.json();
      const logData = await logRes.json();
      setData(cfgData);
      setLogs(logData.logs || []);
    } catch {
      showMsg && showMsg('Failed to load email automation data');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggle = async (field) => {
    if (!data) return;
    const currentVal = data.config?.[field] ?? false;
    const newVal = !currentVal;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/email-automation/config`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: newVal }),
      });
      const d = await res.json();
      setData(prev => ({ ...prev, config: d.config }));
      showMsg && showMsg(`${field === 'automation_enabled' ? 'Automation' : 'Manual sending'} ${newVal ? 'enabled' : 'disabled'}`);
      load();
    } catch {
      showMsg && showMsg('Failed to update setting');
    }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-8 text-[#2B2B2B]/40 text-sm">Loading email automation data…</div>;
  if (!data)   return null;

  const cfg      = data.config || {};
  const envAuto  = data.env_automation_enabled;
  const envSend  = data.env_sending_enabled;
  const dbAuto   = cfg.automation_enabled ?? false;
  const dbSend   = cfg.sending_enabled    ?? true;
  const schedJobs = data.scheduler_jobs   || [];

  // Effective state = env AND db both must be true
  const effectiveAutoEnabled = envAuto && dbAuto;
  const effectiveSendEnabled = envSend && dbSend;

  const sentLogs    = logs.filter(l => l.status === 'sent');
  const blockedLogs = logs.filter(l => l.status?.startsWith('blocked'));
  const autoAttempts = logs.filter(l => l.trigger_source === 'scheduler');

  return (
    <div className="space-y-6" data-testid="email-automation-settings">

      {/* ── Status Overview ── */}
      <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-[#8A9A8B]/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#C8A97E]" />
            <h3 className="font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Email Automation Status
            </h3>
          </div>
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-[#F6F1E9] transition-colors" title="Refresh">
            <RefreshCw size={14} className="text-[#8A9A8B]" />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y divide-[#8A9A8B]/8">
          {[
            { label: 'Automatic Emails', value: <StatusPill on={effectiveAutoEnabled} onLabel="Enabled" offLabel="Disabled" /> },
            { label: 'Manual Sending',   value: <StatusPill on={effectiveSendEnabled} onLabel="Enabled" offLabel="Disabled" /> },
            { label: 'Emails Sent (log)', value: <span className="font-bold text-[#2B2B2B] text-sm">{sentLogs.length}</span> },
            { label: 'Blocked Sends',    value: <span className={`font-bold text-sm ${blockedLogs.length ? 'text-red-500' : 'text-[#2B2B2B]'}`}>{blockedLogs.length}</span> },
          ].map(({ label, value }) => (
            <div key={label} className="px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2B2B2B]/40 mb-1.5">{label}</p>
              {value}
            </div>
          ))}
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Automation toggle */}
        <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={14} className="text-[#C8A97E]" />
                <p className="font-semibold text-[#2B2B2B] text-sm">Automatic Emails</p>
              </div>
              <p className="text-xs text-[#2B2B2B]/50 leading-relaxed">
                Scheduled emails sent by the server (cron jobs, APScheduler). When OFF, no email fires automatically.
              </p>
              {!envAuto && (
                <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                  Hard-disabled by <code>EMAIL_AUTOMATION_ENABLED=false</code> in environment — cannot be re-enabled from UI until env var is changed.
                </p>
              )}
            </div>
            <button
              onClick={() => toggle('automation_enabled')}
              disabled={saving || !envAuto}
              data-testid="toggle-automation-enabled"
              className="flex-shrink-0 mt-0.5"
            >
              {dbAuto && envAuto
                ? <ToggleRight size={28} className="text-[#C8A97E]" />
                : <ToggleLeft  size={28} className="text-[#2B2B2B]/25" />}
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-[#8A9A8B]/10 text-xs text-[#2B2B2B]/45 space-y-1">
            <div className="flex items-center gap-1.5"><span className="font-semibold">Env:</span> <StatusPill on={envAuto} onLabel="true" offLabel="false" /></div>
            <div className="flex items-center gap-1.5"><span className="font-semibold">DB:</span>  <StatusPill on={dbAuto} onLabel="true" offLabel="false" /></div>
            <div className="flex items-center gap-1.5"><span className="font-semibold">Effective:</span> <StatusPill on={effectiveAutoEnabled} onLabel="ON" offLabel="OFF" /></div>
          </div>
        </div>

        {/* Manual sending toggle */}
        <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Mail size={14} className="text-[#C8A97E]" />
                <p className="font-semibold text-[#2B2B2B] text-sm">Manual Sending</p>
              </div>
              <p className="text-xs text-[#2B2B2B]/50 leading-relaxed">
                Admin-initiated sends via Generate / Preview / Send Campaign. Turn OFF as emergency kill switch.
              </p>
              {!envSend && (
                <p className="mt-2 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                  Hard-disabled by <code>EMAIL_SENDING_ENABLED=false</code> in environment — all email is blocked.
                </p>
              )}
            </div>
            <button
              onClick={() => toggle('sending_enabled')}
              disabled={saving}
              data-testid="toggle-sending-enabled"
              className="flex-shrink-0 mt-0.5"
            >
              {dbSend
                ? <ToggleRight size={28} className="text-[#C8A97E]" />
                : <ToggleLeft  size={28} className="text-[#2B2B2B]/25" />}
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-[#8A9A8B]/10 text-xs text-[#2B2B2B]/45 space-y-1">
            <div className="flex items-center gap-1.5"><span className="font-semibold">Env:</span> <StatusPill on={envSend} onLabel="true" offLabel="false" /></div>
            <div className="flex items-center gap-1.5"><span className="font-semibold">DB:</span>  <StatusPill on={dbSend} onLabel="true" offLabel="false" /></div>
          </div>
        </div>
      </div>

      {/* ── Timestamps ── */}
      <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-5">
        <h4 className="font-semibold text-[#2B2B2B] text-sm mb-4 flex items-center gap-2">
          <Clock size={14} className="text-[#C8A97E]" /> Timeline
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Last Auto Attempt',  value: cfg.last_auto_attempt_at,  icon: <Zap size={12} className="text-amber-500" />,   trigger: cfg.last_auto_trigger },
            { label: 'Last Manual Send',   value: cfg.last_manual_send_at,   icon: <User size={12} className="text-[#C8A97E]" /> },
            { label: 'Last Send (any)',    value: cfg.last_send_at,          icon: <Mail size={12} className="text-[#8A9A8B]" />,  subject: cfg.last_send_subject },
          ].map(({ label, value, icon, trigger, subject }) => (
            <div key={label} className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2B2B2B]/40 flex items-center gap-1">{icon}{label}</p>
              <p className="text-sm font-semibold text-[#2B2B2B]">{fmt(value)}</p>
              {trigger && <p className="text-[10px] text-[#2B2B2B]/35">{trigger}</p>}
              {subject && <p className="text-[10px] text-[#2B2B2B]/35 truncate">"{subject}"</p>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Scheduler Jobs ── */}
      <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-5">
        <h4 className="font-semibold text-[#2B2B2B] text-sm mb-4 flex items-center gap-2">
          <Calendar size={14} className="text-[#C8A97E]" /> Active Scheduler Jobs
          <span className="ml-auto text-[10px] text-[#2B2B2B]/40 font-normal capitalize">
            Status: {data.scheduler_status}
          </span>
        </h4>
        {schedJobs.length === 0 ? (
          <p className="text-xs text-[#2B2B2B]/40">No scheduled jobs registered.</p>
        ) : (
          <div className="space-y-2">
            {schedJobs.map(job => (
              <div key={job.id} className="flex items-center justify-between text-xs py-1.5" data-testid="scheduler-job-row">
                <span className="font-semibold text-[#2B2B2B]">{job.id}</span>
                <span className="text-[#2B2B2B]/45">{job.next_run === 'None' ? 'No next run' : `Next: ${fmt(job.next_run)}`}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-[#8A9A8B]/10">
          <p className="text-[10px] text-[#2B2B2B]/35 leading-relaxed">
            <strong>Note:</strong> The <code>send_daily_push_notifications</code> job runs at 09:00 UTC (5 AM Eastern) but its email-sending loop has been permanently removed. It now only sends push notifications. Automatic bulk email is controlled by the flags above.
          </p>
        </div>
      </div>

      {/* ── Email Send Log ── */}
      <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#8A9A8B]/10 flex items-center gap-2">
          <FileText size={14} className="text-[#C8A97E]" />
          <h4 className="font-semibold text-[#2B2B2B] text-sm">Email Send Log</h4>
          <span className="ml-auto text-[10px] text-[#2B2B2B]/40">
            {logs.length} recent entries · auto sends: {autoAttempts.length} · blocked: {blockedLogs.length}
          </span>
        </div>
        {logs.length === 0 ? (
          <p className="text-center py-8 text-[#2B2B2B]/35 text-sm">No email activity logged yet.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {logs.map(log => <LogRow key={log.log_id} log={log} />)}
          </div>
        )}
      </div>
    </div>
  );
}
